import React, { useState, useEffect } from "react";
import axios from "axios";
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import { useSearchParams, useNavigate } from "react-router-dom";

import { API_BASE_URL as API_BASE } from "../config";

function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const codigo = searchParams.get("codigo");

  const [celular, setCelular] = useState("");
  const [usuarioEscolhido, setUsuarioEscolhido] = useState("");
  const [usuariosDisponiveis, setUsuariosDisponiveis] = useState([]);
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmacaoNovaSenha, setConfirmacaoNovaSenha] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(false);
  const [tokenValido, setTokenValido] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMensagem("");
    setErro("");
    setUsuariosDisponiveis([]);
    setUsuarioEscolhido("");

    const celularDigits = celular.replace(/\D/g, '');

    try {
      const response = await axios.post(`${API_BASE}/Auth/verificar-celular`, { celular: celularDigits });
      const encontrados = response.data.usuarios;

      if (encontrados.length === 0) {
        setErro("Nenhum usuário encontrado com esse celular.");
      } else if (encontrados.length === 1) {
        await enviarSolicitacaoReset(celularDigits, encontrados[0]);
      } else {
        setUsuariosDisponiveis(encontrados);
      }
    } catch (err) {
      setErro(err.response?.data?.message || "Erro ao verificar celular.");
    } finally {
      setLoading(false);
    }
  };

  const enviarSolicitacaoReset = async (celularDigits, tipoUsuario) => {
    if (!tipoUsuario) return;
    setLoading(true);
    setMensagem("");
    setErro("");
    try {
      const res = await axios.post(`${API_BASE}/Auth/solicitar-reset-senha`, { 
        celular: celularDigits, 
        tipoUsuario 
      });
      setMensagem(res.data.message || `Link enviado para ${tipoUsuario}`);
      setUsuariosDisponiveis([]);
      setUsuarioEscolhido("");
    } catch (err) {
      setErro(err.response?.data?.message || "Erro ao solicitar reset de senha.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (codigo) {
      setLoading(true);
      axios.get(`${API_BASE}/Auth/validar-reset-token?codigo=${codigo}`)
        .then(response => {
          setTokenValido(true);
          setMensagem(response.data.message);
        })
        .catch(err => {
          setErro(err.response?.data?.message || "Token inválido ou expirado.");
          setTokenValido(false);
        })
        .finally(() => setLoading(false));
    }
  }, [codigo]);

  const handleResetPasswordClick = (e) => {
    e.preventDefault();
    if (novaSenha !== confirmacaoNovaSenha) {
      setErro("As senhas não coincidem.");
      return;
    }
    setConfirmModalVisible(true);
  };

  const confirmarReset = async () => {
    setConfirmModalVisible(false);
    setLoading(true);
    setMensagem("");
    setErro("");

    try {
      await axios.post(`${API_BASE}/Auth/resetar-senha`, { 
        codigo, 
        novaSenha,
        confirmacaoNovaSenha
      });
      setModalVisible(true);
    } catch (err) {
      setErro(err.response?.data?.message || "Erro ao redefinir a senha.");
    } finally {
      setLoading(false);
    }
  };

  const renderSolicitacao = () => (
    <>
      <h3 className="text-center mb-4" style={{ color: '#898989' }}>Esqueci minha senha</h3>
      {mensagem && <Alert variant="success">{mensagem}</Alert>}
      {erro && <Alert variant="danger">{erro}</Alert>}

      <Form onSubmit={handleForgotPassword}>
        <Form.Group className="mb-3">
          <Form.Label>Celular</Form.Label>
          <Form.Control
            type="text"
            placeholder="(11)98765-4321"
            value={celular}
            onChange={e => {
              let val = e.target.value.replace(/\D/g, '');
              if (val.length > 11) val = val.slice(0, 11);
              const masked = val.replace(/^(\d{2})(\d{5})(\d{0,4})$/, '($1)$2-$3').replace(/-$/, '');
              setCelular(masked);
            }}
            required
          />
        </Form.Group>

        {usuariosDisponiveis.length > 1 && (
          <Form.Group className="mb-3">
            <Form.Label>Escolha o usuário</Form.Label>
            <Form.Select 
              value={usuarioEscolhido} 
              onChange={e => setUsuarioEscolhido(e.target.value)} 
              required
            >
              <option value="">Selecione...</option>
              {usuariosDisponiveis.map(u => (
                <option key={u} value={u}>
                  {u === "ClienteMaster" ? "Administrador" : u}
                </option>
              ))}
            </Form.Select>

            <Button 
              type="button" 
              className="w-100 mt-2"
              onClick={() => enviarSolicitacaoReset(celular.replace(/\D/g, ''), usuarioEscolhido)} 
              disabled={!usuarioEscolhido || loading}
              style={{
                background: 'linear-gradient(to right, #898989, #192435ff)',
                border: 'none',
                color: '#fff'
              }}
            >
              {loading ? <Spinner animation="border" size="sm" /> : 'Enviar link de recuperação'}
            </Button>

          </Form.Group>
        )}

        {usuariosDisponiveis.length <= 1 && (
          <Button 
            type="submit" 
            className="w-100"
            disabled={loading}
            style={{ 
              background: 'linear-gradient(to right, #898989, #192435ff)', 
              border: 'none',
              color: '#fff'
            }}
          >
            {loading ? <Spinner animation="border" size="sm" /> : 'Enviar link de recuperação'}
          </Button>
        )}
      </Form>
    </>
  );

  const renderReset = () => (
    <>
      <h3 className="text-center mb-4" style={{ color: '#898989' }}>Redefinir Senha</h3>
      {mensagem && <Alert variant="success">{mensagem}</Alert>}
      {erro && <Alert variant="danger">{erro}</Alert>}

      <Form onSubmit={handleResetPasswordClick}>
        <Form.Group className="mb-3">
          <Form.Label>Nova Senha</Form.Label>
          <Form.Control type="password" value={novaSenha} onChange={e => setNovaSenha(e.target.value)} required />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Confirme a Nova Senha</Form.Label>
          <Form.Control type="password" value={confirmacaoNovaSenha} onChange={e => setConfirmacaoNovaSenha(e.target.value)} required />
        </Form.Group>
        <Button 
          type="submit" 
          className="w-100"
          disabled={loading}
          style={{ 
            background: 'linear-gradient(to right, #6a11cb, #2575fc)', 
            border: 'none',
            color: '#fff'
          }}
        >
          {loading ? <Spinner animation="border" size="sm" /> : 'Redefinir Senha'}
        </Button>
      </Form>
    </>
  );

  return (
    <div 
      style={{ 
        background: 'linear-gradient(to right, #898989, #192435ff)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
    >
      <Container>
        <Row className="justify-content-center">
          <Col md={6} lg={4}>
<Card
        className="shadow-lg"
        style={{
          width: "100%",
          maxWidth: "420px",
          borderRadius: "16px"
        }}
      >              <Card.Body className="p-4">
                {codigo ? (tokenValido ? renderReset() : <Spinner animation="border" />) : renderSolicitacao()}
                <div className="text-center mt-3">
                  <a href="/login">Voltar para o Login</a>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal show={confirmModalVisible} onHide={() => setConfirmModalVisible(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmação</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Você deseja confirmar o reset de senha?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmModalVisible(false)}>Cancelar</Button>
          <Button variant="primary" onClick={confirmarReset}>Confirmar</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={modalVisible} onHide={() => setModalVisible(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Senha redefinida</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Sua senha foi redefinida com sucesso! Você será redirecionado para o login.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => navigate("/login")}>
            Ir para Login
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ForgotPassword;
